import { Nullable } from './globals';

/**
 * Error class name constants for unit limit errors
 */
export const UNIT_LIMIT_ERROR_NAMES = {
  DAILY: 'DailyUnitLimitExceededError',
  WEEKLY: 'WeeklyUnitLimitExceededError',
  MONTHLY: 'MonthlyUnitLimitExceededError',
  BREACHED: 'UnitLimitBreachedError'
} as const;

/**
 * Error message constants for unit limit errors
 */
export const UNIT_LIMIT_ERROR_MESSAGES = {
  DAILY_LIMIT_EXCEEDED: (unitTypeLabel: string, limit: Nullable<number>, current: number) =>
    `Daily ${unitTypeLabel} limit exceeded. Limit: ${limit}, Current: ${current}`,
  DAILY_LIMIT_EXCEEDED_FOR_DAY: (
    unitTypeLabel: string,
    limit: Nullable<number>,
    current: number,
    dayName: string
  ) => `Daily ${unitTypeLabel} limit exceeded for ${dayName}. Limit: ${limit}, Current: ${current}`,
  WEEKLY_LIMIT_EXCEEDED: (unitTypeLabel: string, limit: Nullable<number>, current: number) =>
    `Weekly ${unitTypeLabel} limit exceeded. Limit: ${limit}, Current: ${current}`,
  MONTHLY_LIMIT_EXCEEDED: (unitTypeLabel: string, limit: Nullable<number>, current: number) =>
    `Monthly ${unitTypeLabel} limit exceeded. Limit: ${limit}, Current: ${current}`,
  MONTHLY_LIMIT_EXCEEDED_FOR_MONTH: (
    unitTypeLabel: string,
    limit: Nullable<number>,
    current: number,
    monthName: string
  ) =>
    `Monthly ${unitTypeLabel} limit exceeded for ${monthName}. Limit: ${limit}, Current: ${current}`
};

export class UnitLimitBreachedError extends Error {
  public readonly cause = null;

  constructor(message: string) {
    super(message);
    this.name = UNIT_LIMIT_ERROR_NAMES.BREACHED;
  }
}

/**
 * Error thrown when a daily unit limit is exceeded
 */
export class DailyUnitLimitExceededError extends UnitLimitBreachedError {
  constructor(unitTypeLabel: string, limit: Nullable<number>, current: number, dayName?: string) {
    const message = dayName
      ? UNIT_LIMIT_ERROR_MESSAGES.DAILY_LIMIT_EXCEEDED_FOR_DAY(
          unitTypeLabel,
          limit,
          current,
          dayName
        )
      : UNIT_LIMIT_ERROR_MESSAGES.DAILY_LIMIT_EXCEEDED(unitTypeLabel, limit, current);
    super(message);
    this.name = UNIT_LIMIT_ERROR_NAMES.DAILY;
  }
}

/**
 * Error thrown when a weekly unit limit is exceeded
 */
export class WeeklyUnitLimitExceededError extends UnitLimitBreachedError {
  constructor(unitTypeLabel: string, limit: Nullable<number>, current: number) {
    super(UNIT_LIMIT_ERROR_MESSAGES.WEEKLY_LIMIT_EXCEEDED(unitTypeLabel, limit, current));
    this.name = UNIT_LIMIT_ERROR_NAMES.WEEKLY;
  }
}

/**
 * Error thrown when a monthly unit limit is exceeded
 */
export class MonthlyUnitLimitExceededError extends UnitLimitBreachedError {
  constructor(unitTypeLabel: string, limit: Nullable<number>, current: number, monthName?: string) {
    const message = monthName
      ? UNIT_LIMIT_ERROR_MESSAGES.MONTHLY_LIMIT_EXCEEDED_FOR_MONTH(
          unitTypeLabel,
          limit,
          current,
          monthName
        )
      : UNIT_LIMIT_ERROR_MESSAGES.MONTHLY_LIMIT_EXCEEDED(unitTypeLabel, limit, current);
    super(message);
    this.name = UNIT_LIMIT_ERROR_NAMES.MONTHLY;
  }
}
